import React, { useState } from "react";
import {
  Autocomplete,
  TextField,
  Tooltip,
  AutocompleteRenderInputParams,
  Popper,
  Grow,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { StatusFilterMenuItems } from "../../../types/types";

interface StatusFilterProps {
  onStatusChange: (status: string) => void;
  statusFilterMenuItems: StatusFilterMenuItems[];
  enableTruncation?: boolean;
}

const RealTimeStockStatusFilter = ({
  onStatusChange,
  statusFilterMenuItems,
  enableTruncation = false,
}: StatusFilterProps) => {
  const [selectedStatus, setSelectedStatus] = useState<
    StatusFilterMenuItems | undefined
  >();
  const [open, setOpen] = useState(false);

  const handleChange = (_: any, newValue: StatusFilterMenuItems | null) => {
    setSelectedStatus(newValue ?? undefined);
    onStatusChange(newValue?.id ?? "");
  };

  const truncateStyle:{
    maxWidth: string;
    overflow: string;
    textOverflow: string;
    whiteSpace: any;
} = {
    maxWidth: "160px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const CustomPopper = (props: any) => (
    <Popper
      {...props}
      transition
      placement="bottom-start"
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 4],
          },
        },
      ]}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} timeout={150}>
          <div>{props.children}</div>
        </Grow>
      )}
    </Popper>
  );

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedStatus}
      onChange={handleChange}
      options={statusFilterMenuItems}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      size="small"
      disableClearable
      noOptionsText={
        open && statusFilterMenuItems.length === 0 ? "No options" : ""
      }
      popupIcon={
        <KeyboardArrowDownIcon sx={{ color: "#606060", fontSize: 20 }} />
      }
      PopperComponent={CustomPopper}
      sx={{
        minWidth: "200px",
        maxWidth: enableTruncation ? "200px" : undefined,
        "& .MuiOutlinedInput-root": {
          fontSize: "14px",
          borderRadius: "8px",
          border: "1px solid #DEDEDE",
          paddingRight: "10px",
          "&:hover": {
            borderColor: "#DEDEDE",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#DEDEDE",
          },
          "& .Mui-focused .MuiOutlinedInput-notchedOutline'": {
            boxShadow: "none",
            borderColor: "#DEDEDE",
          },
        },
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          placeholder="Filter by Distributor"
          inputProps={{
            ...params.inputProps,
            style: enableTruncation ? truncateStyle : {},
          }}
        />
      )}
      renderOption={(props, option, { index }) => {
        const labelContent = (
          <div
            style={{
              ...(enableTruncation
                ? { ...truncateStyle, maxWidth: "180px" }
                : {}),
              fontSize: "12px",
              color: "#606060",
              fontFamily: "Poppins",
              width: "100%",
              padding: "8px 16px",
            }}
          >
            {option.label}
          </div>
        );

        return (
          <li
            {...props}
            key={option.id}
            style={{
              borderBottom:
                index !== statusFilterMenuItems.length - 1
                  ? "1px dashed #EBEBEB"
                  : "none",
              margin: "0 7px",
              padding: 0,
              listStyle: "none",
              cursor: "pointer",
              borderRadius: "6px",
              transition: "background 0.2s",
            }}
          >
            {enableTruncation ? (
              <Tooltip title={option.label} placement="right" arrow>
                {labelContent}
              </Tooltip>
            ) : (
              labelContent
            )}
          </li>
        );
      }}
    />
  );
};

export default RealTimeStockStatusFilter;
